import { createSelector } from 'reselect';

import { Hanger, Member, PlatePair } from '@paragon/contract-library';

import { getSelectedItemIdentifiers, getSingleSelectedItemIdentifierOrNull } from '../../library/selection/selectors';
import { SelectableItemType, SelectedItemIdentifier } from '../../library/selection/types';
import { RootState } from '..';
import { isInsertingBearing, isInsertingMember } from '../slices/applicationSlice';
import { getAnalogLinks } from './getAnalogLinks';
import { isInPreviewMode } from './isInPreviewModeSelector';
import { getShouldRenderProfile } from './profileSelectors';

const shouldComponentBeTransparent = createSelector(
  (state: RootState) => state.visualOptions.showAnalogModel,
  (state: RootState) => !!state.application.rewebbingInfo,
  isInPreviewMode,
  getShouldRenderProfile,
  (state: RootState) => isInsertingMember(state.application.insertingObjectInfo),
  (state: RootState) => isInsertingBearing(state.application.insertingObjectInfo),
  (showAnalogModel, isRewebbing, previewMode, shouldRenderProfile, insertingMember, insertingBearing) =>
    showAnalogModel || isRewebbing || previewMode || shouldRenderProfile || insertingMember || insertingBearing,
);

export const getShouldShowLabel = createSelector(
  (state: RootState) => state.visualOptions.showLabels,
  getSelectedItemIdentifiers,
  getSingleSelectedItemIdentifierOrNull,
  getAnalogLinks,
  (showLabels, selectedItems, singleSelectedItem, analogLinks) => (guid: string, type: SelectableItemType) => {
    const componentSelected = componentDesignItemSelected(singleSelectedItem);

    const itemSelected = selectedItems.some((item) => item.guid === guid);

    let linkForThisJointIsSelected = false;
    if (type === 'analogJoint' && singleSelectedItem?.type === 'analogLink' && analogLinks) {
      const link = analogLinks[singleSelectedItem.guid];
      linkForThisJointIsSelected = link != null && (link.iJoint.guid === guid || link.jJoint.guid === guid);
    }

    return showLabels && (itemSelected || componentSelected || linkForThisJointIsSelected);
  },
);

export const getMemberViewStatus = createSelector(
  (state: RootState) => state.visualOptions,
  getSelectedItemIdentifiers,
  getSingleSelectedItemIdentifierOrNull,
  shouldComponentBeTransparent,
  (
    { hiddenObjectGuids, transparentObjectGuids, showFieldInstalledMembers },
    selectedItems,
    singleSelectedItem,
    componentIsTransparent,
  ) =>
    (member: Member) => {
      if (hiddenObjectGuids.includes(member.guid) || (!showFieldInstalledMembers && member.isFieldInstalled))
        return 'Hidden';

      if (transparentObjectGuids.includes(member.guid) || componentIsTransparent) return 'Transparent';

      const selected = selectedItems.some(
        ({ type, guid }) => (type === 'member' || type === 'brace') && guid === member.guid,
      );

      const componentSelected = componentDesignItemSelected(singleSelectedItem);

      return selected || componentSelected ? 'Normal' : 'Transparent';
    },
);

export const getPlatePairViewStatus = createSelector(
  (state: RootState) => state.visualOptions,
  getSelectedItemIdentifiers,
  getSingleSelectedItemIdentifierOrNull,
  shouldComponentBeTransparent,
  (
    { showPlates, hiddenObjectGuids, shearLineViewMode, transparentObjectGuids },
    selectedItems,
    singleSelectedItem,
    componentIsTransparent,
  ) =>
    (platePair: PlatePair) => {
      if (!showPlates) return 'Hidden';

      if (hiddenObjectGuids.includes(platePair.guid) || shearLineViewMode !== 'NONE') return 'Hidden';
      if (componentIsTransparent || transparentObjectGuids.includes(platePair.guid)) return 'Transparent';

      const selected = selectedItems.some((item) => item.type === 'platePair' && item.guid === platePair.guid);
      const componentSelected = componentDesignItemSelected(singleSelectedItem);

      return selected || componentSelected ? 'Normal' : 'Transparent';
    },
);

export const getHangerViewStatus = createSelector(
  getSingleSelectedItemIdentifierOrNull,
  shouldComponentBeTransparent,
  (selectedItem, componentIsTransparent) => (hanger: Hanger) => {
    if (componentIsTransparent) return 'Wireframe';

    const selected = selectedItem?.type === 'hanger' && selectedItem.guid === hanger.guid;
    const componentSelected = componentDesignItemSelected(selectedItem);

    return selected || componentSelected ? 'Normal' : 'Wireframe';
  },
);

function componentDesignItemSelected(singleSelectedItem: SelectedItemIdentifier | null) {
  return singleSelectedItem?.type === 'componentDesign' || singleSelectedItem?.type === 'productionComponentInstance';
}
